import { Group, GroupMember, CreateGroupDTO, Organization, CreateOrganizationDTO, AddMemberDTO } from '../models/Group';
import { Collection, CreateCollectionDTO } from '../models/Collection';
import { GroupInvite, CreateInviteDTO } from '../models/Invite';
import { BASE_URL } from '../constants/global';

const API_BASE = BASE_URL || '';

async function fetchWithAuth(url: string, options: RequestInit = {}, token: string) {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP error! status: ${response.status}`);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null;
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return null;
}

// Organization API
export const organizationApi = {
  getAll: (token: string): Promise<Organization[]> =>
    fetchWithAuth('/v1/organizations', {}, token),

  getById: (id: number, token: string): Promise<Organization> =>
    fetchWithAuth(`/v1/organizations/${id}`, {}, token),

  create: (data: CreateOrganizationDTO, token: string): Promise<Organization> =>
    fetchWithAuth('/v1/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),

  update: (id: number, data: CreateOrganizationDTO, token: string): Promise<Organization> =>
    fetchWithAuth(`/v1/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token),

  search: (query: string, token: string): Promise<Organization[]> =>
    fetchWithAuth(`/v1/organizations/search?q=${encodeURIComponent(query)}`, {}, token),

  delete: (id: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/organizations/${id}`, { method: 'DELETE' }, token),
};

// Group API
export const groupApi = {
  getMyGroups: (token: string): Promise<Group[]> =>
    fetchWithAuth('/v1/groups/my', {}, token),

  getPublicGroups: (token: string): Promise<Group[]> =>
    fetchWithAuth('/v1/groups/public', {}, token),

  getAllGroups: (token: string): Promise<Group[]> =>
    fetchWithAuth('/v1/groups', {}, token),

  getById: (id: number, token: string): Promise<Group> =>
    fetchWithAuth(`/v1/groups/${id}`, {}, token),

  create: (data: CreateGroupDTO, token: string): Promise<Group> =>
    fetchWithAuth('/v1/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),

  update: (id: number, data: CreateGroupDTO, token: string): Promise<Group> =>
    fetchWithAuth(`/v1/groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token),

  delete: (id: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/groups/${id}`, { method: 'DELETE' }, token),

  search: (query: string, token: string): Promise<Group[]> =>
    fetchWithAuth(`/v1/groups/search?q=${encodeURIComponent(query)}`, {}, token),

  getByOrganization: (orgId: number, token: string): Promise<Group[]> =>
    fetchWithAuth(`/v1/groups/organization/${orgId}`, {}, token),

  // Members
  getMembers: (groupId: number, token: string): Promise<GroupMember[]> =>
    fetchWithAuth(`/v1/groups/${groupId}/members`, {}, token),

  addMember: (groupId: number, data: AddMemberDTO, token: string): Promise<void> =>
    fetchWithAuth(`/v1/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),

  updateMemberRole: (groupId: number, userId: number, role: string, token: string): Promise<void> =>
    fetchWithAuth(`/v1/groups/${groupId}/members/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ userId, role }),
    }, token),

  removeMember: (groupId: number, userId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/groups/${groupId}/members/${userId}`, { method: 'DELETE' }, token),

  leaveGroup: (groupId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/groups/${groupId}/leave`, { method: 'POST' }, token),

  // Organizations
  addOrganization: (groupId: number, orgId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/groups/${groupId}/organizations/${orgId}`, { method: 'POST' }, token),

  removeOrganization: (groupId: number, orgId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/groups/${groupId}/organizations/${orgId}`, { method: 'DELETE' }, token),

  isAdmin: (groupId: number, token: string): Promise<boolean> =>
    fetchWithAuth(`/v1/groups/${groupId}/is-admin`, {}, token),

  isMember: (groupId: number, token: string): Promise<boolean> =>
    fetchWithAuth(`/v1/groups/${groupId}/is-member`, {}, token),

  joinPublicGroup: (groupId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/groups/${groupId}/join`, { method: 'POST' }, token),
};

// Collection API
export const collectionApi = {
  getMyCollections: (token: string): Promise<Collection[]> =>
    fetchWithAuth('/v1/collections/my', {}, token),

  getAccessible: (token: string): Promise<Collection[]> =>
    fetchWithAuth('/v1/collections/accessible', {}, token),

  getById: (id: number, token: string): Promise<Collection> =>
    fetchWithAuth(`/v1/collections/${id}`, {}, token),

  getByGroup: (groupId: number, token: string): Promise<Collection[]> =>
    fetchWithAuth(`/v1/collections/group/${groupId}`, {}, token),

  create: (data: CreateCollectionDTO, token: string): Promise<Collection> =>
    fetchWithAuth('/v1/collections', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),

  update: (id: number, data: CreateCollectionDTO, token: string): Promise<Collection> =>
    fetchWithAuth(`/v1/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token),

  delete: (id: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/collections/${id}`, { method: 'DELETE' }, token),

  search: (query: string, token: string): Promise<Collection[]> =>
    fetchWithAuth(`/v1/collections/search?q=${encodeURIComponent(query)}`, {}, token),

  getByLabel: (label: string, token: string): Promise<Collection[]> =>
    fetchWithAuth(`/v1/collections/label/${encodeURIComponent(label)}`, {}, token),

  getLabels: (token: string): Promise<string[]> =>
    fetchWithAuth('/v1/collections/labels', {}, token),

  // Images
  addImage: (collectionId: number, imageId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/collections/${collectionId}/images/${imageId}`, { method: 'POST' }, token),

  addImages: (collectionId: number, imageIds: number[], token: string): Promise<void> =>
    fetchWithAuth(`/v1/collections/${collectionId}/images`, {
      method: 'POST',
      body: JSON.stringify(imageIds),
    }, token),

  removeImage: (collectionId: number, imageId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/collections/${collectionId}/images/${imageId}`, { method: 'DELETE' }, token),

  getImageIds: (collectionId: number, token: string): Promise<number[]> =>
    fetchWithAuth(`/v1/collections/${collectionId}/images`, {}, token),

  getPreviewImageIds: (collectionId: number, limit: number, token: string): Promise<number[]> =>
    fetchWithAuth(`/v1/collections/${collectionId}/images?limit=${limit}`, {}, token),

  // Labels
  addLabel: (collectionId: number, label: string, token: string): Promise<void> =>
    fetchWithAuth(`/v1/collections/${collectionId}/labels/${encodeURIComponent(label)}`, { method: 'POST' }, token),

  removeLabel: (collectionId: number, label: string, token: string): Promise<void> =>
    fetchWithAuth(`/v1/collections/${collectionId}/labels/${encodeURIComponent(label)}`, { method: 'DELETE' }, token),

  // Sharing
  share: (collectionId: number, groupId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/collections/${collectionId}/share/${groupId}`, { method: 'POST' }, token),

  unshare: (collectionId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/collections/${collectionId}/unshare`, { method: 'POST' }, token),

  getImagesByIds: (imageIds: number[], token: string): Promise<any[]> =>
    fetchWithAuth('/v1/image/batch', {
      method: 'POST',
      body: JSON.stringify(imageIds),
    }, token),
};

// Invite API
export const inviteApi = {
  create: (data: CreateInviteDTO, token: string): Promise<GroupInvite> =>
    fetchWithAuth('/v1/invites', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token),

  getByToken: (inviteToken: string, token: string): Promise<GroupInvite> =>
    fetchWithAuth(`/v1/invites/token/${inviteToken}`, {}, token),

  getMyPending: (token: string): Promise<GroupInvite[]> =>
    fetchWithAuth('/v1/invites/my/pending', {}, token),

  getGroupInvites: (groupId: number, token: string): Promise<GroupInvite[]> =>
    fetchWithAuth(`/v1/invites/group/${groupId}`, {}, token),

  getPendingGroupInvites: (groupId: number, token: string): Promise<GroupInvite[]> =>
    fetchWithAuth(`/v1/invites/group/${groupId}/pending`, {}, token),

  accept: (inviteToken: string, token: string): Promise<void> =>
    fetchWithAuth(`/v1/invites/accept/${inviteToken}`, { method: 'POST' }, token),

  decline: (inviteToken: string, token: string): Promise<void> =>
    fetchWithAuth(`/v1/invites/decline/${inviteToken}`, { method: 'POST' }, token),

  cancel: (inviteId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/invites/${inviteId}`, { method: 'DELETE' }, token),

  resend: (inviteId: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/invites/${inviteId}/resend`, { method: 'POST' }, token),
};

// Export API
export const exportApi = {
  exportCollectionToExcel: async (collectionId: number, token: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE}/v1/export/collection/${collectionId}/excel`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to export collection');
    return response.blob();
  },

  exportImagesToExcel: async (imageIds: number[], token: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE}/v1/export/images/excel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(imageIds),
    });
    if (!response.ok) throw new Error('Failed to export images');
    return response.blob();
  },

  exportImageToPdf: async (imageId: number, token: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE}/v1/export/image/${imageId}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to export image');
    return response.blob();
  },

  exportImagesToPdf: async (imageIds: number[], token: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE}/v1/export/images/pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(imageIds),
    });
    if (!response.ok) throw new Error('Failed to export images');
    return response.blob();
  },
};

// Admin API
export const adminApi = {
  checkSuperAdmin: (token: string): Promise<{ isSuperAdmin: boolean }> =>
    fetchWithAuth('/v1/admin/check', {}, token),

  getAllUsers: (token: string): Promise<any[]> =>
    fetchWithAuth('/v1/admin/users', {}, token),

  toggleSuperAdmin: (userId: number, isSuperAdmin: boolean, token: string): Promise<void> =>
    fetchWithAuth(`/v1/admin/users/${userId}/super-admin?isSuperAdmin=${isSuperAdmin}`, { method: 'PUT' }, token),

  getAllGroups: (token: string): Promise<Group[]> =>
    fetchWithAuth('/v1/admin/groups', {}, token),

  getStats: (token: string): Promise<{ totalUsers: number; totalGroups: number; totalImages: number }> =>
    fetchWithAuth('/v1/admin/stats', {}, token),

  purgeUserData: (
    userId: number,
    options: { dryRun?: boolean; confirm?: string } = {},
    token: string
  ): Promise<any> => {
    const params = new URLSearchParams();
    if (options.dryRun) params.set('dryRun', 'true');
    if (options.confirm) params.set('confirm', options.confirm);
    const qs = params.toString();
    return fetchWithAuth(
      `/v1/admin/users/${userId}/purge-data${qs ? `?${qs}` : ''}`,
      { method: 'POST' },
      token
    );
  },

  // Error Center
  getErrorLogs: (
    token: string,
    params: {
      page?: number;
      size?: number;
      errorCode?: string;
      errorType?: string;
      isResolved?: boolean;
      isIos?: boolean;
      userEmail?: string;
      startDateTs?: number;
      endDateTs?: number;
      isSuccess?: boolean;
      correlationId?: string;
      errorSource?: string;
    } = {}
  ): Promise<any> => {
    const qs = new URLSearchParams();
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.size !== undefined) qs.set('size', String(params.size));
    if (params.errorCode) qs.set('errorCode', params.errorCode);
    if (params.errorType) qs.set('errorType', params.errorType);
    if (params.isResolved !== undefined) qs.set('isResolved', String(params.isResolved));
    if (params.isIos !== undefined) qs.set('isIos', String(params.isIos));
    if (params.userEmail) qs.set('userEmail', params.userEmail);
    if (params.startDateTs) qs.set('startDateTs', String(params.startDateTs));
    if (params.endDateTs) qs.set('endDateTs', String(params.endDateTs));
    if (params.isSuccess !== undefined) qs.set('isSuccess', String(params.isSuccess));
    if (params.correlationId) qs.set('correlationId', params.correlationId);
    if (params.errorSource) qs.set('errorSource', params.errorSource);
    return fetchWithAuth(`/v1/admin/errors?${qs.toString()}`, {}, token);
  },

  getErrorLogById: (id: number, token: string): Promise<any> =>
    fetchWithAuth(`/v1/admin/errors/${id}`, {}, token),

  getErrorStats: (token: string): Promise<any> =>
    fetchWithAuth('/v1/admin/errors/stats', {}, token),

  resolveError: (id: number, resolutionNotes: string, token: string): Promise<any> =>
    fetchWithAuth(`/v1/admin/errors/${id}/resolve`, {
      method: 'PUT',
      body: JSON.stringify({ resolutionNotes }),
    }, token),

  deleteErrorLog: (id: number, token: string): Promise<void> =>
    fetchWithAuth(`/v1/admin/errors/${id}`, { method: 'DELETE' }, token),

  getTopErrorUsers: (token: string, limit = 10): Promise<any> =>
    fetchWithAuth(`/v1/admin/errors/top-users?limit=${limit}`, {}, token),

  getErrorHeatmap: (token: string, days = 30): Promise<any> =>
    fetchWithAuth(`/v1/admin/errors/heatmap?days=${days}`, {}, token),

  getErrorRate: (token: string): Promise<any> =>
    fetchWithAuth('/v1/admin/errors/rate', {}, token),

  getErrorsByCorrelationId: (correlationId: string, token: string): Promise<any> =>
    fetchWithAuth(`/v1/admin/errors/correlation/${correlationId}`, {}, token),

  getGroupedErrors: (token: string, limit = 20): Promise<any> =>
    fetchWithAuth(`/v1/admin/errors/grouped?limit=${limit}`, {}, token),

  replayAnalysis: (errorLogId: number, token: string): Promise<any> =>
    fetchWithAuth(`/v1/admin/errors/replay/${errorLogId}`, { method: 'POST' }, token),
};
